import classNames from 'classnames';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { RBACGroup } from '../../../types';
import {
  graphApi,
  useAddGroupToGroupMutation,
  useRemoveGroupMemberMutation,
} from '../../graph/services/graphApi';
import { errorMessage, withTransientRetry } from '../../graph/transientRetry';
import {
  selectACLPendingAdds,
  selectACLPendingDeletes,
  removeRoleFromACLPendingAdds,
  removeRoleFromACLPendingDeletes,
} from '../services/aclMembershipSlice';

type Props = {
  acl?: RBACGroup;
  // On-prem writeback ACLs are managed in AD — keep Submit disabled even if a stray
  // pending change somehow exists.
  readOnly?: boolean;
};

export default function ACLSubmitChanges({ acl, readOnly = false }: Props) {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  // Role ids whose add/remove errored on the last submit. Successful items are dropped
  // from pending state, so the rows that linger after a partial failure ARE these — we
  // track the set explicitly to badge them and keep the modal open for a retry.
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());

  const pendingAdds = useAppSelector(selectACLPendingAdds);
  const pendingDeletes = useAppSelector(selectACLPendingDeletes);

  const [addGroupToGroup] = useAddGroupToGroupMutation();
  const [removeGroupMember] = useRemoveGroupMemberMutation();

  const hasPendingChanges = pendingAdds.length > 0 || pendingDeletes.length > 0;

  const modalClassName = classNames('modal fixed inset-0 flex items-center justify-center', {
    'modal-open': showModal,
  });

  const showModalButtonClassName = classNames('btn btn-primary', {
    'btn-disabled': !hasPendingChanges || readOnly,
  });

  const handleSubmit = async () => {
    if (!acl) return;
    const aclId = acl.id;

    // Patch the role-members list cache on success rather than refetch. Neither mutation
    // invalidates (ADR-0002): Graph's members index lags the write, so a refetch reads
    // stale — a removed role re-appears, or a just-added role vanishes (taking its
    // effective users with it, since the effective list is derived from this list). Patch
    // optimistically here; the (hidden) RefreshACLData button re-syncs server truth.
    const patchAdd = (role: RBACGroup) =>
      dispatch(
        graphApi.util.updateQueryData('getACLGroupRoleMembers', aclId, (draft) => {
          if (!draft.some((g) => g.id === role.id)) {
            draft.push({
              description: 'description' in role ? role.description : undefined,
              displayName: role.displayName,
              id: role.id,
              transitive: false,
            });
            draft.sort((a, b) => a.displayName.localeCompare(b.displayName));
          }
        })
      );
    const patchRemove = (role: RBACGroup) =>
      dispatch(
        graphApi.util.updateQueryData('getACLGroupRoleMembers', aclId, (draft) =>
          draft.filter((g) => g.id !== role.id)
        )
      );

    // Clear last attempt's failures so a retry starts clean.
    setFailedIds(new Set());

    // Graph can reject a membership write with a transient concurrent-modification 404
    // (or throttle with 429/503) while the same ACL is being modified — retry those
    // before surfacing the error, matching the Roles page. See #67 / #83.
    const addPromises = pendingAdds.map(async (role): Promise<string | null> => {
      try {
        await withTransientRetry(() =>
          addGroupToGroup({ groupId: aclId, memberId: role.id }).unwrap()
        );
        patchAdd(role);
        dispatch(removeRoleFromACLPendingAdds(role));
        return null;
      } catch (error) {
        toast(
          `Error adding ${role.displayName} to ${acl.displayName}: ${errorMessage(error) ?? 'Unknown error'}`,
          { type: 'error' }
        );
        return role.id;
      }
    });

    const deletePromises = pendingDeletes.map(async (role): Promise<string | null> => {
      try {
        await withTransientRetry(() =>
          removeGroupMember({
            groupDisplayName: role.displayName,
            groupId: aclId,
            memberId: role.id,
          }).unwrap()
        );
        patchRemove(role);
        dispatch(removeRoleFromACLPendingDeletes(role));
        return null;
      } catch (error) {
        toast(
          `Error removing ${role.displayName} from ${acl.displayName}: ${errorMessage(error) ?? 'Unknown error'}`,
          { type: 'error' }
        );
        return role.id;
      }
    });

    const failed = (await Promise.all([...addPromises, ...deletePromises])).filter(
      (id): id is string => id !== null
    );

    // Close only on full success. On a partial failure the failed items stay pending
    // (we never dispatched their removal); keep the modal open and badge them so the
    // user sees what to retry rather than learning only from the transient toast.
    if (failed.length === 0) {
      setShowModal(false);
    } else {
      setFailedIds(new Set(failed));
    }
  };

  if (!acl) {
    return <div></div>;
  }

  return (
    <>
      <button
        className={showModalButtonClassName}
        onClick={() => {
          setFailedIds(new Set());
          setShowModal(true);
        }}
      >
        Submit
      </button>

      <div className={modalClassName}>
        <div className="bg-base-100 rounded-lg p-5">
          <div className="font-bold text-xl mb-2">Confirm changes to {acl.displayName}</div>
          {failedIds.size > 0 && (
            <div className="alert alert-error mb-2 text-sm py-2">
              Some changes failed and are still pending. The affected items are marked below — fix
              the cause and submit again.
            </div>
          )}
          <div className="pb-2">
            {pendingAdds.length > 0 && (
              <div className="py-2">
                <div className="pb-1">Role groups to add:</div>
                <div className="border border-base-200 rounded-lg overflow-hidden">
                  <ul className="divide-y divide-base-200">
                    {pendingAdds.map((role) => (
                      <li
                        className="text-success-content bg-success p-2 flex justify-between items-center gap-2"
                        key={role.id}
                      >
                        <span>{role.displayName}</span>
                        {failedIds.has(role.id) && (
                          <span className="badge badge-error badge-sm whitespace-nowrap">
                            Failed
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {pendingDeletes.length > 0 && (
              <div className="py-2">
                <div className="pb-1">Role groups to remove:</div>
                <div className="border border-base-200 rounded-lg overflow-hidden">
                  <ul className="divide-y divide-base-200">
                    {pendingDeletes.map((role) => (
                      <li
                        className="text-error-content bg-error p-2 flex justify-between items-center gap-2"
                        key={role.id}
                      >
                        <span>{role.displayName}</span>
                        {failedIds.has(role.id) && (
                          <span className="badge badge-warning badge-sm whitespace-nowrap">
                            Failed
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div className="text-base-content mb-4">
            Are you sure you want to submit all pending changes?
          </div>
          <div className="flex justify-end">
            <button className="btn btn-ghost mr-2" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
